import { useEffect, useState } from 'react';
import { call, getToken } from './api.js';
import { withReturn, hereNow } from './gate.jsx';
import { TabBar } from './ui.jsx';

/* ══════════════════════════════════════════════════════════════════════════
 * RESERVATIONS — `08b_Reservations.html`
 *
 * ⚠️ AN ELEATER COULD MAKE A RESERVATION AND NEVER SEE IT AGAIN. The screen
 * was locked, Help pointed at it twice — *"its status shows in Reservations on
 * your profile"*, *"Open it in Reservations"* — and there was no route to fill
 * it with. Twelve routes on the reservations module and every one was the
 * restaurant's or acted on a single id. `GET /reservations/mine` was added the
 * same night this screen was built.
 *
 * ── ⚠️ WHAT A ROW IS ALLOWED TO CLAIM ───────────────────────────────────────
 *
 * The glossary keeps three separate words and this screen keeps them apart:
 *
 *   reservation_click     they tapped through. INTENT.
 *   self_reported_booking they came back and said they booked it.
 *   confirmed_reservation Eleat holds it AND the restaurant accepted.
 *
 * Routing Disclosure clause 3: *"'Yes, I made a reservation' means only that
 * the Eleater told Eleat they completed a reservation. It is not confirmation
 * from the provider or restaurant."*
 *
 * ⚠️ SO A SELF-REPORTED BOOKING IS NEVER SHOWN AS CONFIRMED. Help makes the
 * promise in Eleat's own voice — *"Until the restaurant responds, it is a
 * request — Eleat never tells you a table is held when it has not been."* A
 * green tick on this screen over a booking the restaurant has not seen is the
 * single most damaging thing Eleat could put in front of somebody: they turn
 * up, there is no table, and it was Eleat that told them there was.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️ THREE WORDS. THEY ARE THE LOCKED SCREEN'S, NOT MINE.
 *
 * `08b_Reservations.html` decides this in its own script, and it allows
 * exactly three:
 *
 *     class="status …">  CANCELLED · COMPLETED · REQUESTED
 *
 * cancelled → CANCELLED. in the past → COMPLETED. anything else → REQUESTED.
 *
 * ⚠️ I WROTE "You told us you booked" HERE AND IT WAS AN INVENTION. Al, 22
 * September 2026: *"Since when do we use the terminology 'you told us you
 * booked'? That is never, never, never in our English phrasing or vocabulary.
 * We are supposed to be using restaurant terminology and if you dig deeper,
 * this should not be in there."*
 *
 * He is right twice over. It was RULE 4 — copy I made up rather than found —
 * and the answer was sitting in the locked screen I had already opened to take
 * the empty states from. **Dig before writing.**
 *
 * ── AND "REQUESTED" SOLVES THE PROBLEM I INVENTED A SENTENCE FOR ────────────
 *
 * I had tied myself in knots trying to word the difference between a booking
 * Eleat holds and one the Eleater says they made on the restaurant's own
 * provider. The locked screen never had that problem, because **REQUESTED is
 * true of both**: until the restaurant responds, it is a request. It is also
 * exactly what a restaurant calls it.
 *
 * So nothing on this screen can ever claim a table is held — not because a
 * rule forbids it, but because the word for it does not exist here. That is a
 * better design than the one I was building, and it was already written.
 * ══════════════════════════════════════════════════════════════════════════ */
function statusOf(r) {
  if (r.cancelled_at || r.status === 'cancelled') return { label: 'CANCELLED', tone: 'cancelled' };
  const past = r.reserved_for && new Date(r.reserved_for) < new Date();
  if (past || r.status === 'completed') return { label: 'COMPLETED', tone: 'done' };
  /* ⚠️ CONFIRMED ONLY WHEN THE RESTAURANT ITSELF PRESSED IT — Al-Noor, 9 Oct
     2026, yes to "the restaurant taps Confirmed… should it say Confirmed?".
     `status = 'confirmed'` is written by the restaurant's desk (setDeskStatus,
     Restaurant Admin + Lite) and by nothing the Eleater does: their own "yes,
     the restaurant replied" is `outcome` and stays REQUESTED, which keeps the
     Routing Disclosure true (a self-report is not a confirmation). */
  if (r.status === 'confirmed') return { label: 'CONFIRMED', tone: 'ok' };
  return { label: 'REQUESTED', tone: 'ok' };
}

/* ⚠️ THE VENUE'S CLOCK, NOT THE PHONE'S — the same rule as the venue page. An
   Eleater in Calgary looking at a Hong Kong booking must see the Hong Kong
   time, because that is when they have to be there. */
function whenText(r) {
  if (!r.reserved_for) return null;
  try {
    /* the venue's own clock choice (151 time_format), as on its page —
       "Sun 11 Oct, 7:30 PM", or 19:30 where the restaurant chose 24-hour.
       It printed 19:30 for every venue (look walk, 9 Oct 2026). */
    const at = new Date(r.reserved_for);
    const tz = r.timezone || undefined;
    const day = new Intl.DateTimeFormat('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', timeZone: tz }).format(at);
    const time = r.time_format === '24h'
      ? new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: tz }).format(at)
      : new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz }).format(at);
    return `${day}, ${time}`;
  } catch {
    return new Date(r.reserved_for).toLocaleString();
  }
}

function isUpcoming(r) {
  if (r.cancelled_at || r.status === 'cancelled') return false;
  if (!r.reserved_for) return true;      // no time yet — still live
  return new Date(r.reserved_for) >= new Date();
}

export default function Reservations() {
  const signedIn = !!getToken();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [which, setWhich] = useState('upcoming');
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    call('/reservations/mine', { auth: true })
      .then((d) => { if (live) setRows(d.reservations || []); })
      .catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [signedIn]);

  async function cancel(r) {
    /* ⚠️ CANCELLING IN ELEAT DOES NOT CANCEL AT THE RESTAURANT when the
       booking was made on the restaurant's own provider — Eleat never held
       that table and cannot release it. Saying so out loud is the whole point:
       somebody who thinks they have cancelled and has not is a no-show, and
       the restaurant loses the cover. */
    const external = !r.is_eleat_booking;
    const msg = external
      ? 'This removes it from Eleat. You still need to cancel with the restaurant — Eleat did not make this booking.'
      : 'Cancel this reservation?';
    if (!window.confirm(msg)) return;
    setBusy(r.id);
    try {
      await call(`/reservations/${encodeURIComponent(r.id)}/cancel`, { method: 'POST', auth: true });
      setRows((cur) => cur.map((x) => (x.id === r.id
        ? { ...x, status: 'cancelled', cancelled_at: new Date().toISOString() } : x)));
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(null); }
  }

  if (!signedIn) {
    return (
      <div className="home">
        <div className="top">
          <a className="back" href="?profile=1" aria-label="Back">‹</a>
          <h1 className="heroline">Reservations</h1>
        </div>
        <main>
          <>
          <p className="mut">
            Sign in to see your reservations.
          </p>
          <div className="guest">
            <a className="gcta" href={withReturn('?welcome=1', hereNow())}>Sign in or join Eleat</a>
          </div>
          </>
        </main>
        <TabBar active="profile" />
      </div>
    );
  }

  const all = rows || [];
  const list = all.filter((r) => (which === 'upcoming' ? isUpcoming(r) : !isUpcoming(r)));

  return (
    <div className="home resvlist">
      <div className="top">
        <a className="back" href="?profile=1" aria-label="Back">‹</a>
        <h1 className="heroline">Reservations</h1>
      </div>

      <main>
        {/* the locked screen's two tabs, its words and its order */}
        <div className="chipbox">
          {[['upcoming', 'Upcoming'], ['past', 'Past']].map(([k, label]) => (
            <button key={k} type="button"
              className={which === k ? 'chip on' : 'chip'}
              aria-pressed={which === k}
              onClick={() => setWhich(k)}>{label}</button>
          ))}
        </div>

        {err && <div className="err" aria-live="polite">{err}</div>}
        {rows === null && !err && <p className="mut">Loading…</p>}

        {rows !== null && list.length === 0 && (
          /* verbatim, both empty states */
          which === 'upcoming' ? (
            <>
              <p className="lead">No upcoming reservations</p>
              <p className="mut">When you book a table through Eleat, it shows up here.</p>
            </>
          ) : (
            <>
              <p className="lead">No past reservations</p>
              <p className="mut">Your dining history will appear here.</p>
            </>
          )
        )}

        {list.map((r) => {
          const s = statusOf(r);
          const when = whenText(r);
          return (
            <div key={r.id} className="rrow">
              {/* 08b_Reservations.html `.rh`: the name, and its status pill on the right */}
              <div className="rh">
                <a className="rname" href={`?venue=${encodeURIComponent(r.restaurant_id)}`}>
                  {r.restaurant_name}
                </a>
                <span className={`rstat ${s.tone}`}>{s.label}</span>
              </div>
              <div className="meta">
                {[when, r.party_size ? `${r.party_size} people` : null]
                  .filter(Boolean).join(' · ')}
              </div>

              {/* ⚠️ THE PROVIDER IS NAMED, NEVER HARD-CODED. It is a field on
                  the reservation — Eats365, OpenTable, whoever the restaurant
                  uses. Al, 21 September: "it's not just the one." */}
              {r.provider && !r.is_eleat_booking && (
                <div className="mut">{r.booked_in_eleat ? `Booked through ${r.provider}` : `Requested through ${r.provider}`}</div>
              )}

              {r.dietary_notes && (
                /* ⚠️ ALLERGIES ARE SHOWN BACK, because somebody who typed
                   "shellfish" wants to see that it was carried. */
                <div className="mut">Allergies: {r.dietary_notes}</div>
              )}
              {r.special_requests && <div className="mut">{r.special_requests}</div>}

              {isUpcoming(r) && !r.cancelled_at && (
                <button className="forgot" type="button"
                  disabled={busy === r.id}
                  onClick={() => cancel(r)}>Cancel</button>
              )}
            </div>
          );
        })}
      </main>

      <TabBar active="profile" />
    </div>
  );
}
